import React, { useEffect, useState, useRef } from "react";
import { X, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

export default function CommentsDrawer({ campaign, onClose, onMarkedRead }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [lastReadAt, setLastReadAt] = useState(null);
  const [onLoadUnread, setOnLoadUnread] = useState(new Set());
  const endRef = useRef(null);

  const myType = user?.type || (user?.email?.endsWith("@wishlink.com") ? "admin" : "brand");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${campaign.id}/comments`);
      setComments(data.comments);
      setLastReadAt(data.last_read_at || null);
      // Snapshot: the comments that were unread the moment the drawer opened. We freeze
      // this so we can keep highlighting them as the user scrolls, even after we mark them
      // read on the server.
      const otherType = myType === "admin" ? "brand" : "admin";
      const lr = data.last_read_at || "";
      const unread = new Set(
        (data.comments || []).filter(
          (c) => !c.deleted && c.user_type === otherType && (!lr || c.created_at > lr)
        ).map((c) => c.id)
      );
      setOnLoadUnread(unread);
      // Tell the server "I've now seen everything up to now"
      try { await api.post(`/campaigns/${campaign.id}/comments/read`); }
      catch (e) { console.error("[CommentsDrawer] mark-as-read failed", e); }
      if (onMarkedRead) onMarkedRead();
    }
    catch { toast.error("Failed to load comments"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaign.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [comments]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/campaigns/${campaign.id}/comments`, { comment_text: text });
      setComments([...comments, data.comment]);
      setText("");
    } catch { toast.error("Failed to add comment"); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm("Delete this comment? The thread will show 'Comment deleted by you'.")) return;
    try {
      await api.delete(`/campaigns/${campaign.id}/comments/${c.id}`);
      // Update locally without re-fetching
      setComments((arr) => arr.map((x) => x.id === c.id ? { ...x, deleted: true, deleted_by: user?.email, deleted_at: new Date().toISOString() } : x));
      toast.success("Comment deleted");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Failed to delete");
    }
  };

  const canDelete = (c) => !c.deleted && (user?.type === "admin" || c.user_email === user?.email);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-[#1a1f36]/30 wl-fade-in" onClick={onClose} />
      <aside className="fixed top-0 right-0 bottom-0 w-[440px] max-w-full bg-white border-l border-[var(--border-default)] z-50 wl-slide-in flex flex-col" data-testid="comments-drawer">
        <header className="px-6 py-5 border-b border-[var(--border-default)] flex items-start justify-between">
          <div>
            <div className="wl-label">Comments</div>
            <h2 className="font-display text-lg font-semibold mt-0.5">{campaign.campaign_name}</h2>
            <div className="text-xs text-[var(--text-secondary)] mt-0.5">{comments.filter(c => !c.deleted).length} message{comments.filter(c => !c.deleted).length === 1 ? "" : "s"}</div>
          </div>
          <button onClick={onClose} className="wl-btn-ghost" data-testid="drawer-close"><X size={18} /></button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="comments-thread">
          {loading ? <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="wl-skeleton h-16" />)}</div>
          : comments.length === 0 ? <div className="text-sm text-[var(--text-secondary)] text-center py-12">No comments yet. Start the conversation.</div>
          : comments.map((c) => c.deleted ? (
              <div key={c.id} className="p-3 rounded-xl border border-dashed border-[var(--border-dashed)] text-center" data-testid={`comment-deleted-${c.id}`}>
                <div className="text-xs text-[var(--text-secondary)] italic">
                  Comment deleted by {c.deleted_by === user?.email ? "you" : c.deleted_by}
                </div>
              </div>
            ) : (() => {
            const isWishlink = c.user_email?.endsWith("@wishlink.com") || c.user_type === "admin";
            const isUnread = onLoadUnread.has(c.id);
            return (
              <div
                key={c.id}
                className={`p-3 rounded-xl group relative transition-all ${isWishlink ? "" : "bg-[var(--bg-page)]"} ${isUnread ? "ring-2 ring-[var(--brand)]/40 shadow-[0_4px_14px_-6px_rgba(232,97,58,0.35)]" : ""}`}
                style={isWishlink ? { background: "var(--brand-tint)" } : {}}
                data-testid={`comment-${c.id}`}
              >
                {isUnread && (
                  <span className="absolute -top-1.5 -left-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[var(--brand)] text-white" data-testid={`comment-unread-${c.id}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> NEW
                  </span>
                )}
                <div className="flex items-baseline justify-between mb-1 pr-6">
                  <div className={`text-sm ${isUnread ? "font-bold" : "font-semibold"}`}>{c.user_email}</div>
                  <div className="text-[11px] text-[var(--text-secondary)]">{new Date(c.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</div>
                </div>
                <div className={`text-sm leading-relaxed text-[var(--text-primary)] whitespace-pre-wrap ${isUnread ? "font-semibold" : ""}`}>{c.comment_text}</div>
                {canDelete(c) && (
                  <button onClick={() => remove(c)} className="absolute top-2 right-2 p-1 rounded text-[var(--text-secondary)] opacity-0 group-hover:opacity-100 hover:text-[var(--brand)] transition-opacity" data-testid={`comment-delete-${c.id}`} title="Delete comment">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );
          })())}
          <div ref={endRef} />
        </div>

        <form onSubmit={submit} className="px-6 py-4 border-t border-[var(--border-default)] flex items-center gap-2">
          <input className="wl-input flex-1" placeholder="Add a comment…" value={text} onChange={(e) => setText(e.target.value)} disabled={busy} data-testid="comment-input" />
          <button className="wl-btn-primary" disabled={busy || !text.trim()} data-testid="comment-submit">Send</button>
        </form>
      </aside>
    </>
  );
}
